import { loadGoogleMapsScript } from './googleMapsLoader';

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000;
    const p1 = (lat1 * Math.PI) / 180;
    const p2 = (lat2 * Math.PI) / 180;
    const dp = ((lat2 - lat1) * Math.PI) / 180;
    const dl = ((lng2 - lng1) * Math.PI) / 180;
    const a =
        Math.sin(dp / 2) * Math.sin(dp / 2) +
        Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) * Math.sin(dl / 2);
    return 2 * R * Math.asin(Math.sqrt(Math.min(1, a)));
}

function pickComponent(
    components: google.maps.GeocoderAddressComponent[],
    ...typeGroups: string[][]
): string | undefined {
    for (const types of typeGroups) {
        const c = components.find((x) => types.some((t) => x.types.includes(t)));
        if (c?.long_name) return c.long_name;
    }
    return undefined;
}

/** Human-readable area, e.g. "Clifton, Karachi" from reverse geocode. */
function composeAreaLabel(components: google.maps.GeocoderAddressComponent[]): string {
    const neighborhood = pickComponent(components, ['neighborhood', 'sublocality_level_2']);
    const sublocality = pickComponent(
        components,
        ['sublocality', 'sublocality_level_1', 'administrative_area_level_3']
    );
    const locality = pickComponent(components, ['locality']);
    const parts = [neighborhood, sublocality, locality].filter(Boolean) as string[];
    const uniq: string[] = [];
    for (const p of parts) {
        if (uniq[uniq.length - 1] !== p) uniq.push(p);
    }
    if (uniq.length >= 2) return uniq.slice(0, 2).join(', ');
    if (uniq.length === 1) return uniq[0]!;
    const admin2 = pickComponent(components, ['administrative_area_level_2']);
    if (admin2 && locality && admin2 !== locality) return `${locality}, ${admin2}`;
    return locality || admin2 || '';
}

/** Short bank label for CSV: "Meezan Bank - XYZ" → "Meezan Bank". */
function shortBankName(raw: string): string {
    const s = raw.trim();
    if (!s) return '';
    let x = s.split(/\s+-\s+/)[0]?.split(',')[0]?.trim() || s;
    x = x.replace(/\s+ATM\s*$/i, '').replace(/\s+Branch\s*$/i, '').trim();
    return x;
}

function bankDedupeKey(label: string): string {
    return label
        .toLowerCase()
        .replace(/\bbank\b/g, '')
        .replace(/[^a-z0-9]+/g, '')
        .trim();
}

/**
 * Reverse-geocode coordinates to a short area/neighborhood string.
 */
export async function fetchNearestAreaLabel(
    apiKey: string,
    lat: number,
    lng: number
): Promise<string | null> {
    const key = apiKey.trim();
    if (!key || Number.isNaN(lat) || Number.isNaN(lng)) return null;

    await loadGoogleMapsScript(key);
    const geocoder = new google.maps.Geocoder();
    const results = await new Promise<google.maps.GeocoderResult[]>((resolve) => {
        geocoder.geocode({ location: { lat, lng } }, (res, status) => {
            if (status === 'OK' && res?.length) resolve(res);
            else resolve([]);
        });
    });
    const first = results[0];
    if (!first?.address_components?.length) {
        const fb = first?.formatted_address?.trim();
        if (fb) {
            const parts = fb.split(',').map((p) => p.trim()).filter(Boolean);
            return parts.slice(0, 2).join(', ') || fb;
        }
        return null;
    }
    const label = composeAreaLabel(first.address_components).trim();
    if (label) return label;
    const parts = first.formatted_address?.split(',').map((p) => p.trim()).filter(Boolean) || [];
    return parts.slice(0, 2).join(', ') || null;
}

const MAX_BANKS = 6;
const BANK_SEARCH_RADIUS_M = 3500;

/**
 * Nearby banks as a comma-separated list, e.g. "Meezan Bank, UBL, HBL".
 */
export async function fetchNearbyBankNamesCsv(
    apiKey: string,
    lat: number,
    lng: number
): Promise<string | null> {
    const key = apiKey.trim();
    if (!key || Number.isNaN(lat) || Number.isNaN(lng)) return null;

    await loadGoogleMapsScript(key);
    const svc = new google.maps.places.PlacesService(document.createElement('div'));
    const origin = new google.maps.LatLng(lat, lng);

    let rows = await new Promise<google.maps.places.PlaceResult[]>((resolve) => {
        svc.nearbySearch(
            {
                location: origin,
                radius: BANK_SEARCH_RADIUS_M,
                type: 'bank',
            },
            (results, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && results?.length) {
                    resolve(results as google.maps.places.PlaceResult[]);
                } else {
                    resolve([]);
                }
            }
        );
    });

    if (!rows.length) {
        rows = await new Promise<google.maps.places.PlaceResult[]>((resolve) => {
            svc.textSearch(
                {
                    query: 'bank',
                    location: origin,
                    radius: BANK_SEARCH_RADIUS_M,
                },
                (results, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK && results?.length) {
                        resolve(results as google.maps.places.PlaceResult[]);
                    } else {
                        resolve([]);
                    }
                }
            );
        });
    }

    const withDist = rows
        .map((r) => {
            const loc = r.geometry?.location;
            if (!loc || !r.name) return null;
            const d = haversineMeters(lat, lng, loc.lat(), loc.lng());
            return { name: r.name, d };
        })
        .filter((x): x is { name: string; d: number } => x != null)
        .sort((a, b) => a.d - b.d);

    const seen = new Set<string>();
    const labels: string[] = [];
    for (const { name } of withDist) {
        const label = shortBankName(name);
        if (!label) continue;
        const k = bankDedupeKey(label);
        if (!k || seen.has(k)) continue;
        seen.add(k);
        labels.push(label);
        if (labels.length >= MAX_BANKS) break;
    }

    return labels.length ? labels.join(', ') : null;
}
